import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Headphones,
  Users,
  HelpCircle,
  BarChart3,
  Bot,
  Shield,
  Sparkles,
  Zap,
  MessageCircle,
  Phone,
  Package,
  Star,
  Rocket,
  Target,
  TrendingUp,
  Award,
  CheckCircle,
  Lightbulb,
  ArrowRight,
  Copy,
  Check,
  MessageSquare,
} from "lucide-react";
import { toast } from "sonner";
import ChatWidget from "@/components/ChatWidget";
import { API } from "@/lib/api";
import { LANDING } from "@/constants/testIds";

// Available icons for use-case cards (must match backend ICON_OPTIONS list)
const ICON_MAP = {
  Headphones, Users, HelpCircle, BarChart3, Bot, Shield,
  Sparkles, Zap, MessageCircle, Phone, Package, Star,
  Rocket, Target, TrendingUp, Award, CheckCircle, Lightbulb,
};

// Fallback defaults used while /api/config is still loading (or if fetch fails)
const DEFAULT_CONFIG = {
  brand_name: "Chatbot AI",
  nav_subtitle: "Widget AI untuk website Anda",
  whatsapp_link: "",
  hero_badge: "Chatbot AI 24/7 — Powered by LLM",
  hero_title_top: "Jawab pengunjung otomatis.",
  hero_title_highlight: "Tangkap leads",
  hero_title_tail: " tanpa henti.",
  hero_description: "",
  hero_cta_primary: "Coba Chatbot Sekarang",
  hero_cta_secondary: "Lihat Cara Embed",
  hero_stats: [],
  hero_visual_kicker: "Chatbot Widget",
  hero_visual_title: "Bantu pengunjung 24 jam",
  hero_minicard_name: "",
  hero_minicard_desc: "",
  hero_minicard_time: "",
  usecases_kicker: "Kegunaan",
  usecases_title: "Satu widget, banyak manfaat",
  usecases: [],
  embed_kicker: "Instalasi 1 Menit",
  embed_title: "Embed chatbot ke website Anda",
  embed_description: "",
  embed_features: [],
  embed_note: "",
  footer_tagline: "",
  footer_copyright: "© {year} Chatbot AI. All rights reserved.",
};

function EmbedCodeBlock({ brandName }) {
  const [copied, setCopied] = useState(false);
  const backendUrl = process.env.REACT_APP_BACKEND_URL || "";
  const code = `<!-- ${brandName} Widget -->
<script src="${backendUrl.replace(/\/$/, "")}/embed.js" defer></script>`;

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(code);
    } catch (e) {
      const ta = document.createElement("textarea");
      ta.value = code;
      document.body.appendChild(ta);
      ta.select();
      try { document.execCommand("copy"); } catch (_err) { /* noop */ }
      document.body.removeChild(ta);
    }
    setCopied(true);
    toast.success("Kode berhasil disalin");
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="relative bg-slate-900 rounded-2xl p-5 border border-slate-800">
      <button
        data-testid={LANDING.copyEmbedBtn}
        onClick={copy}
        className="absolute top-4 right-4 flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-slate-800 hover:bg-slate-700 text-slate-200 text-xs font-medium transition-colors"
      >
        {copied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
        {copied ? "Tersalin" : "Salin"}
      </button>
      <pre
        data-testid={LANDING.embedCode}
        className="text-xs sm:text-sm text-slate-100 overflow-x-auto font-mono leading-relaxed pt-6"
      >
        <code>{code}</code>
      </pre>
    </div>
  );
}

export default function Landing() {
  const [config, setConfig] = useState(DEFAULT_CONFIG);

  useEffect(() => {
    fetch(`${API}/config`)
      .then((r) => r.json())
      .then((c) => setConfig({ ...DEFAULT_CONFIG, ...c }))
      .catch(() => {});
  }, []);

  const c = config;
  const copyrightText = (c.footer_copyright || "").replace("{year}", new Date().getFullYear());

  return (
    <div className="min-h-screen bg-[#F8FAFC] text-slate-900 relative">
      {/* Nav */}
      <nav className="sticky top-0 z-40 bg-white/85 backdrop-blur-md border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <div>
              <div className="font-display font-bold text-[15px] leading-none tracking-tight">
                {c.brand_name}
              </div>
              <div className="text-[10px] text-slate-500 leading-none mt-1">
                {c.nav_subtitle}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Link
              data-testid={LANDING.adminLink}
              to="/admin"
              className="text-sm text-slate-600 hover:text-slate-900 px-3 py-2 rounded-lg hover:bg-slate-100 transition-colors font-medium"
            >
              Admin
            </Link>
            {c.whatsapp_link && (
              <a
                href={c.whatsapp_link}
                target="_blank"
                rel="noopener noreferrer"
                className="text-sm bg-[#25D366] hover:bg-[#1EBE5A] text-white px-3 py-2 rounded-lg font-medium transition-colors hidden sm:inline-block"
              >
                Chat WhatsApp
              </a>
            )}
          </div>
        </div>
      </nav>

      {/* Hero */}
      <section className="max-w-7xl mx-auto px-6 pt-16 sm:pt-24 pb-20 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
        <div className="lg:col-span-7">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <div className="inline-flex items-center gap-2 bg-red-50 border border-red-200 rounded-full px-3 py-1 text-xs font-medium text-red-700 mb-6">
              <Sparkles className="w-3.5 h-3.5" />
              {c.hero_badge}
            </div>
            <h1 className="font-display font-bold text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.05] text-slate-900">
              {c.hero_title_top}
              <br />
              <span className="text-[#DC2626]">{c.hero_title_highlight}</span>
              {c.hero_title_tail}
            </h1>
            <p className="mt-6 text-base sm:text-lg text-slate-600 max-w-2xl leading-relaxed whitespace-pre-line">
              {c.hero_description}
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <button
                data-testid={LANDING.openChatCta}
                onClick={() => {
                  const btn = document.querySelector(`[data-testid="chat-widget-launcher"]`);
                  btn?.click();
                }}
                className="group inline-flex items-center gap-2 bg-[#DC2626] hover:bg-[#B91C1C] text-white px-6 py-3 rounded-xl font-medium text-sm transition-colors"
              >
                <MessageSquare className="w-4 h-4" />
                {c.hero_cta_primary}
                <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
              </button>
              <a
                href="#embed"
                data-testid={LANDING.heroCta}
                className="inline-flex items-center gap-2 bg-white border border-slate-200 hover:border-slate-300 text-slate-900 px-6 py-3 rounded-xl font-medium text-sm transition-colors"
              >
                {c.hero_cta_secondary}
              </a>
            </div>

            <div className="mt-10 grid grid-cols-2 sm:grid-cols-4 gap-4 max-w-2xl">
              {(c.hero_stats || []).map((s, i) => (
                <div key={i} className="border-l-2 border-[#DC2626] pl-3">
                  <div className="font-display text-2xl font-bold text-slate-900">{s.value}</div>
                  <div className="text-xs text-slate-500 mt-0.5">{s.label}</div>
                </div>
              ))}
            </div>
          </motion.div>
        </div>

        {/* Right visual */}
        <motion.div
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.6, delay: 0.15 }}
          className="lg:col-span-5 relative"
        >
          <div className="relative aspect-[4/5] rounded-3xl overflow-hidden border border-slate-200 shadow-[0_20px_60px_-15px_rgba(220,38,38,0.25)] bg-gradient-to-br from-[#DC2626] to-[#7F1D1D]">
            <div
              className="absolute inset-0 opacity-20"
              style={{
                backgroundImage:
                  "radial-gradient(circle at 20% 20%, rgba(255,255,255,0.4) 0%, transparent 40%), radial-gradient(circle at 80% 80%, rgba(255,255,255,0.3) 0%, transparent 40%)",
              }}
            />
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="w-40 h-40 rounded-3xl bg-white/10 backdrop-blur-sm border border-white/20 flex items-center justify-center">
                <Bot className="w-24 h-24 text-white" strokeWidth={1.5} />
              </div>
            </div>
            <div className="absolute bottom-6 left-6 right-6 text-white">
              <div className="text-[11px] uppercase tracking-widest opacity-80 font-medium">
                {c.hero_visual_kicker}
              </div>
              <div className="font-display text-2xl font-bold mt-1 leading-tight">
                {c.hero_visual_title}
              </div>
            </div>
          </div>
          {/* Floating mini-card */}
          {(c.hero_minicard_name || c.hero_minicard_desc) && (
            <div className="absolute -left-4 sm:-left-8 top-1/4 bg-white rounded-2xl border border-slate-200 shadow-[0_8px_32px_rgba(0,0,0,0.08)] p-4 max-w-[220px] hidden sm:block">
              <div className="flex items-center gap-2 mb-2">
                <div className="w-2 h-2 rounded-full bg-emerald-400" />
                <div className="text-xs text-slate-500">Lead baru masuk</div>
              </div>
              <div className="font-semibold text-sm text-slate-900">{c.hero_minicard_name}</div>
              <div className="text-xs text-slate-500">{c.hero_minicard_desc}</div>
              {c.hero_minicard_time && (
                <div className="text-xs text-slate-400 mt-1">{c.hero_minicard_time}</div>
              )}
            </div>
          )}
        </motion.div>
      </section>

      {/* Use Cases */}
      <section className="max-w-7xl mx-auto px-6 py-16 border-t border-slate-200">
        <div className="max-w-2xl">
          <div className="text-xs uppercase tracking-widest text-[#DC2626] font-semibold mb-3">
            {c.usecases_kicker}
          </div>
          <h2 className="font-display text-3xl sm:text-4xl font-bold tracking-tight text-slate-900">
            {c.usecases_title}
          </h2>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mt-10">
          {(c.usecases || []).map((u, i) => {
            const Icon = ICON_MAP[u.icon] || Bot;
            return (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.08 }}
                className="group bg-white border border-slate-200 hover:border-[#DC2626]/40 rounded-2xl p-6 transition-colors relative overflow-hidden"
              >
                <div className="relative">
                  <div className="w-11 h-11 rounded-xl bg-[#DC2626] text-white flex items-center justify-center mb-4">
                    <Icon className="w-5 h-5" strokeWidth={2} />
                  </div>
                  <div className="font-display font-bold text-lg text-slate-900">{u.title}</div>
                  <div className="text-sm text-slate-500 mt-1.5 leading-relaxed whitespace-pre-line">
                    {u.desc}
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>
      </section>

      {/* Embed section */}
      <section id="embed" className="max-w-7xl mx-auto px-6 py-20 border-t border-slate-200">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-start">
          <div className="lg:col-span-5">
            <div className="text-xs uppercase tracking-widest text-[#DC2626] font-semibold mb-3">
              {c.embed_kicker}
            </div>
            <h2 className="font-display text-3xl sm:text-4xl font-bold tracking-tight text-slate-900">
              {c.embed_title}
            </h2>
            <p className="text-slate-600 mt-4 leading-relaxed whitespace-pre-line">
              {c.embed_description}
            </p>
            <ul className="mt-6 space-y-3">
              {(c.embed_features || []).filter(Boolean).map((f, i) => (
                <li key={i} className="flex items-start gap-3 text-sm text-slate-700">
                  <div className="w-5 h-5 rounded-full bg-[#DC2626]/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <Check className="w-3 h-3 text-[#DC2626]" strokeWidth={3} />
                  </div>
                  {f}
                </li>
              ))}
            </ul>
          </div>
          <div className="lg:col-span-7">
            <EmbedCodeBlock brandName={c.brand_name} />
            {c.embed_note && (
              <div className="mt-4 flex items-start gap-3 p-4 bg-red-50 border border-red-200 rounded-xl">
                <Shield className="w-5 h-5 text-red-700 flex-shrink-0 mt-0.5" />
                <div className="text-sm text-red-900 whitespace-pre-line">{c.embed_note}</div>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-slate-200 mt-10">
        <div className="max-w-7xl mx-auto px-6 py-10 flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
          <div>
            <div className="font-display font-bold text-sm">{c.brand_name}</div>
            <div className="text-xs text-slate-500 mt-1">{c.footer_tagline}</div>
          </div>
          <div className="flex items-center gap-3">
            <Link to="/admin" className="text-sm text-slate-600 hover:text-[#DC2626] transition-colors">
              Dashboard Admin
            </Link>
            {c.whatsapp_link && (
              <a
                href={c.whatsapp_link}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 bg-[#25D366] hover:bg-[#1EBE5A] text-white text-xs font-medium px-3 py-2 rounded-lg transition-colors"
              >
                <Phone className="w-3 h-3" /> WhatsApp
              </a>
            )}
          </div>
        </div>
        <div className="border-t border-slate-200 py-4 text-center text-xs text-slate-400">
          {copyrightText}
        </div>
      </footer>

      <ChatWidget config={config} />
    </div>
  );
}
